import * as XLSX from 'xlsx'
import { MONTH_NAMES } from './projectAnalysisPeriod'
import type { ProjectMixProjectRow } from '../types/projectAnalysis'
import type { ProjectDetailStats } from '../types/projectAnalysis'

export function safeFilename(base: string) {
  return (
    (base || 'export')
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '_')
      .slice(0, 80) || 'export'
  )
}

export function getOverviewExportRows(list: ProjectMixProjectRow[]) {
  return list.map((p, idx) => {
    const entries = Number(p.entries_count) || 0
    const late = Number(p.late_entries) || 0
    const latePct = entries > 0 ? (late / entries) * 100 : null
    const leaveDays =
      typeof p.leave_days === 'number'
        ? Math.round(p.leave_days * 10) / 10
        : (p.leave_days ?? 0)
    return {
      'S.no': idx + 1,
      Project: p.project_name || `Project ${p.project_id}`,
      Client: (p.client_name || '').trim() || '—',
      Category: (p.project_category || '').trim() || '—',
      'Project ID': p.project_id,
      'Total hrs':
        typeof p.total_hours === 'number'
          ? Math.round(p.total_hours * 10) / 10
          : (p.total_hours ?? '—'),
      'Billable hrs':
        typeof p.billable_hours === 'number'
          ? Math.round(p.billable_hours * 10) / 10
          : (p.billable_hours ?? '—'),
      'Non-bill. hrs':
        typeof p.non_billable_hours === 'number'
          ? Math.round(p.non_billable_hours * 10) / 10
          : (p.non_billable_hours ?? '—'),
      'Util. %':
        typeof p.utilization_pct === 'number'
          ? `${(Math.round(p.utilization_pct * 10) / 10).toFixed(1)}%`
          : '—',
      'Late %': latePct != null ? `${latePct.toFixed(1)}%` : '—',
      'Leave days': leaveDays,
    }
  })
}

export function exportOverviewCsv(
  rows: ReturnType<typeof getOverviewExportRows>,
  labelParts: string[],
) {
  if (rows.length === 0) return
  const headers = Object.keys(rows[0])
  const csvLine = (arr: string[]) =>
    arr
      .map((v) => {
        const s = String(v ?? '')
        if (/[,"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`
        return s
      })
      .join(',')
  const bom = '\uFEFF'
  const csv =
    bom +
    csvLine(headers) +
    '\n' +
    rows.map((r) => csvLine(headers.map((h) => String(r[h as keyof typeof r])))).join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `project_wise_hours_${safeFilename(labelParts.filter(Boolean).join('_') || 'overview')}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

export function exportOverviewExcel(
  rows: ReturnType<typeof getOverviewExportRows>,
  labelParts: string[],
) {
  if (rows.length === 0) return
  const ws = XLSX.utils.json_to_sheet(rows)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Project-wise Hours')
  XLSX.writeFile(
    wb,
    `project_wise_hours_${safeFilename(labelParts.filter(Boolean).join('_') || 'overview')}.xlsx`,
  )
}

const RESOURCE_EXPORT_HEADERS = [
  'Project',
  'Resource',
  'Period',
  'Billable hrs',
  'Non-bill. hrs',
  'Total hrs',
  'Leave days',
  'Late #',
  'Late %',
  'Util %',
] as const

export function getResourcesExportRows(
  stats: ProjectDetailStats,
  selectedProjectId: number,
) {
  const breakdown = stats.userMonthlyBreakdown
  const projectName =
    stats.projectInfo.project_name ||
    (selectedProjectId != null ? `Project ${selectedProjectId}` : '—')
  const rows: Record<string, string | number>[] = []
  stats.usersData.forEach((u) => {
    const resourceName = u.full_name || u.username || `User ${u.user_id}`
    const monthlyRows = breakdown?.get(u.user_id) || []
    monthlyRows.forEach((row) => {
      const rowEntries = Number(row.entries_count) || 0
      const rowLateCount = Number(row.late_entries_count) || 0
      const rowLatePct =
        rowEntries > 0 ? Math.min(100, (rowLateCount / rowEntries) * 100) : 0
      const rowLeave = Math.round((row.leave_days ?? 0) * 10) / 10
      const periodLabel = `${MONTH_NAMES[(row.month || 1) - 1]} ${row.year}`
      const rowUtil = row.util ?? 0
      rows.push({
        Project: projectName,
        Resource: resourceName,
        Period: periodLabel,
        'Billable hrs': Math.round(row.billable_hours * 100) / 100,
        'Non-bill. hrs': Math.round((row.non_billable_hours ?? 0) * 100) / 100,
        'Total hrs': Math.round(row.total_hours * 100) / 100,
        'Leave days': rowLeave,
        'Late #': rowLateCount,
        'Late %': Math.round(rowLatePct * 10) / 10,
        'Util %': Math.round(rowUtil * 10) / 10,
      })
    })
  })
  return rows
}

export function exportResourcesCsv(
  rows: Record<string, string | number>[],
  projectDetailName: string,
) {
  if (rows.length === 0) return
  const headers = [...RESOURCE_EXPORT_HEADERS]
  const escape = (v: string | number) => {
    const s = String(v ?? '')
    if (/[,"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`
    return s
  }
  const line = (obj: Record<string, string | number>) =>
    headers.map((h) => escape(obj[h])).join(',')
  const csv =
    '\uFEFF' + headers.join(',') + '\r\n' + rows.map(line).join('\r\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `project_resources_${safeFilename(projectDetailName)}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

export function exportResourcesExcel(
  rows: Record<string, string | number>[],
  projectDetailName: string,
) {
  if (rows.length === 0) return
  const ws = XLSX.utils.json_to_sheet(rows, {
    header: [...RESOURCE_EXPORT_HEADERS],
  })
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Resources')
  XLSX.writeFile(
    wb,
    `project_resources_${safeFilename(projectDetailName)}.xlsx`,
  )
}

export function getMonthlyExportRows(stats: ProjectDetailStats) {
  if (!stats.monthlyData?.length) return []
  return stats.monthlyData.map((m) => {
    const util = m.total_hours > 0 ? (m.billable_hours / m.total_hours) * 100 : 0
    const entries = Number(m.entries_count) || 0
    const late = Number(m.late_entries_count) || 0
    const latePct = entries > 0 ? (late / entries) * 100 : null
    const leaveDays = Math.round((m.leave_days ?? 0) * 10) / 10
    return {
      Period: `${MONTH_NAMES[(m.month || 1) - 1]} ${m.year}`,
      'Total hrs': Math.round(m.total_hours * 100) / 100,
      Billable: Math.round(m.billable_hours * 100) / 100,
      'Non-bill.': Math.round(m.non_billable_hours * 100) / 100,
      'Util. %': `${util.toFixed(0)}%`,
      'Late %': latePct != null ? `${latePct.toFixed(1)}%` : '—',
      'Leave days': leaveDays,
      Resources: m.unique_users ?? '—',
    }
  })
}

export function exportMonthlyCsv(
  rows: ReturnType<typeof getMonthlyExportRows>,
  projectDetailName: string,
) {
  if (rows.length === 0) return
  const headers = Object.keys(rows[0])
  const csvLine = (arr: string[]) =>
    arr
      .map((v) => {
        const s = String(v ?? '')
        if (/[,"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`
        return s
      })
      .join(',')
  const bom = '\uFEFF'
  const csv =
    bom +
    csvLine(headers) +
    '\n' +
    rows.map((r) => csvLine(headers.map((h) => String(r[h as keyof typeof r])))).join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `project_monthly_${safeFilename(projectDetailName)}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

export function exportMonthlyExcel(
  rows: ReturnType<typeof getMonthlyExportRows>,
  projectDetailName: string,
) {
  if (rows.length === 0) return
  const ws = XLSX.utils.json_to_sheet(rows)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Monthly')
  XLSX.writeFile(wb, `project_monthly_${safeFilename(projectDetailName)}.xlsx`)
}
